/**
 * AIS vessel layer state + polling engine (spec 16). Plain polling — the
 * old-browser floor rules out relying on WebSocket/SSE (plans/README
 * conventions) — every 15 s while the layer is on, zoom ≥ 10, and the tab is
 * visible; debounced re-fetch when the viewport pans out of the last
 * fetched bbox. Situational awareness only: no CPA/TCPA, no alarms
 * (recorded no-alarms decision, plans/README.md).
 *
 * The interval/debounce/fetch machinery lives in module-level closures (same
 * shape as `searchStore.ts`'s debounce state) so it's plain functions,
 * testable without a DOM — `hidden` is a store field the browser-only
 * `visibilitychange` listener (wired in `AisLayer.tsx`) sets, rather than
 * this module reading `document` itself.
 */
import { create } from 'zustand';
import { aisResponse, type AisVessel } from '@boatnav/schemas';

export interface AisBbox {
  west: number;
  south: number;
  east: number;
  north: number;
}
export interface AisViewport {
  bbox: AisBbox;
  zoom: number;
}

export const AIS_POLL_MS = 15_000;
export const AIS_MOVE_DEBOUNCE_MS = 1_000;
export const AIS_MIN_ZOOM = 10;

const STORAGE_KEY = 'los.aisOn';

function loadOn(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

interface AisState {
  on: boolean;
  vessels: AisVessel[];
  /** True when the last poll attempt failed — the toggle shows a degraded
   *  state; existing vessels are kept and age out via the opacity/drop rule
   *  rather than being cleared (spec 16: "no error dialogs at sea"). */
  degraded: boolean;
  hidden: boolean;
  selected: AisVessel | null;
  toggle: () => void;
  setHidden: (hidden: boolean) => void;
  selectVessel: (v: AisVessel) => void;
  clearSelected: () => void;
}

export const useAisStore = create<AisState>((set, get) => ({
  on: loadOn(),
  vessels: [],
  degraded: false,
  hidden: false,
  selected: null,

  toggle: () => {
    const on = !get().on;
    try {
      localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch {
      /* ignore */
    }
    set(on ? { on } : { on, vessels: [], degraded: false, selected: null });
  },

  setHidden: (hidden) => set({ hidden }),
  selectVessel: (v) => set({ selected: v }),
  clearSelected: () => set({ selected: null }),
}));

// --- Polling engine ---------------------------------------------------

let intervalId: ReturnType<typeof setInterval> | null = null;
let debounceId: ReturnType<typeof setTimeout> | null = null;
let getViewport: (() => AisViewport | null) | null = null;
let lastFetchedBbox: AisBbox | null = null;

function activeNow(): boolean {
  const s = useAisStore.getState();
  if (!s.on || s.hidden) return false;
  const vp = getViewport?.();
  return vp != null && vp.zoom >= AIS_MIN_ZOOM;
}

async function fetchViewport(bbox: AisBbox): Promise<void> {
  lastFetchedBbox = bbox;
  try {
    const url = `/api/ais?bbox=${bbox.west},${bbox.south},${bbox.east},${bbox.north}`;
    const res = await fetch(url);
    if (!res.ok) {
      useAisStore.setState({ degraded: true });
      return;
    }
    const data = aisResponse.parse(await res.json());
    useAisStore.setState({ vessels: data.vessels, degraded: false });
  } catch {
    // Offline / parse failure — keep the last vessels, they age out via the
    // client-side opacity/drop rule; the toggle shows the degraded state.
    useAisStore.setState({ degraded: true });
  }
}

function tick(): void {
  if (!activeNow()) return;
  const vp = getViewport?.();
  if (!vp) return;
  void fetchViewport(vp.bbox);
}

/** Starts/stops the interval to match current on/hidden/zoom state — the
 *  single place that decides whether polling should be running right now. */
function syncInterval(fetchImmediatelyOnStart: boolean): void {
  const active = activeNow();
  if (active && intervalId == null) {
    intervalId = setInterval(tick, AIS_POLL_MS);
    if (fetchImmediatelyOnStart) tick();
  } else if (!active && intervalId != null) {
    clearInterval(intervalId);
    intervalId = null;
    lastFetchedBbox = null;
  }
}

/** Mounted by `AisLayer` while the layer is on. `getViewportFn` reads the
 *  live map bounds/zoom from `MapController` at call time. */
export function startAisPolling(getViewportFn: () => AisViewport | null): void {
  getViewport = getViewportFn;
  syncInterval(true);
}

export function stopAisPolling(): void {
  if (intervalId != null) {
    clearInterval(intervalId);
    intervalId = null;
  }
  if (debounceId != null) {
    clearTimeout(debounceId);
    debounceId = null;
  }
  getViewport = null;
  lastFetchedBbox = null;
}

/** Call whenever the store's `on`/`hidden` state changes while polling is
 *  (potentially) active, so the interval starts/stops without waiting for a
 *  map move — e.g. flipping the toggle back on, or the tab regaining
 *  visibility, resumes immediately rather than up to 15 s later. */
export function reconcileAisPolling(): void {
  if (getViewport == null) return; // not started (layer off / unmounted)
  syncInterval(true);
}

function bboxLeft(current: AisBbox, last: AisBbox): boolean {
  return current.west < last.west || current.south < last.south || current.east > last.east || current.north > last.north;
}

/** Wire to `MapController.onViewChange` while the layer is on (spec 16):
 *  debounced ~1 s, re-fetches immediately only if the viewport has actually
 *  panned outside the bbox last fetched — a small in-cell nudge doesn't
 *  trigger a request 15 s early. */
export function notifyAisViewportChanged(): void {
  if (debounceId != null) clearTimeout(debounceId);
  debounceId = setTimeout(() => {
    debounceId = null;
    syncInterval(false);
    if (!activeNow()) return;
    const vp = getViewport?.();
    if (!vp) return;
    if (lastFetchedBbox == null || bboxLeft(vp.bbox, lastFetchedBbox)) {
      void fetchViewport(vp.bbox);
    }
  }, AIS_MOVE_DEBOUNCE_MS);
}
